import { FC, useRef } from 'react'
import { useRouter } from 'next/router'
import { LngLatBounds, MapEvent } from 'mapbox-gl'
import { polygon } from '@turf/helpers'
import { localizedStrings } from '@core/strings'
import { useGetProject } from '@core/react-query/features/projects/project'
import { useProjectMapConfiguration } from '@core/react-query/features/projects/maps'
import { Box, Flex } from '@fundamentals'
import { PageTitle } from '@elements'
import { Map } from '@elements/Geolocation'
import { Card, Button, IconButton, Tab, Tabs } from '@common/material'
import { MapTabPanel } from './components/MapTabPanel'
import { LayersTabPanel, LayersTabPanelRef } from './components/LayersTabPanel'
import { DisclaimerTabPanel } from './components/DisclaimerTabPanel'
import { ArrowBack, Clear } from '@mui/icons-material'
import { FeaturePropertyEditor } from './components/FeaturePropertyEditor'
import axios from 'axios'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import {
  PMConfiguratorTab,
  PMDrawMode,
  useProjectMapConfigurator,
} from '@modules/geolocation'
import { PageUrls } from '@core/page-urls'

type ProjectMapConfiguratorProps = {
  organisationId: number
  projectId: number
}

export const ProjectMapConfigurator: FC<ProjectMapConfiguratorProps> = ({
  organisationId,
  projectId,
}) => {
  const router = useRouter()
  const { flags } = useFeatureFlags(['web_project_map_configurator_edit_mode'])
  const layersTabRef = useRef<LayersTabPanelRef>(null)

  const {
    mapRef,
    drawRef,
    activeTab,
    setActiveTab,
    drawMode,
    setDrawMode,
    addLayerToMap,
    changeDrawMode,
    selectFeatureOnMap,
  } = useProjectMapConfigurator()

  const handleOnTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const { data: project } = useGetProject({ projectId })
  const { data: projectMapConfiguration } = useProjectMapConfiguration(
    {
      projectId,
    },
    {
      onSuccess: (data) => {
        // Update layers in child component when data is fetched/refetched
        if (data?.layers) {
          layersTabRef.current?.updateLayers(data.layers)
        }
      },
    },
  )

  const handleOnToggleEditMode = () => {
    //Change Mode: Edit mode
    if (drawMode === PMDrawMode.SELECT_FEATURE) {
      changeDrawMode(PMDrawMode.SIMPLE_SELECT)

      return
    }

    // Default mode: Select mode
    setDrawMode(PMDrawMode.SELECT_FEATURE)
    drawRef.current?.changeMode(PMDrawMode.SELECT_FEATURE, {
      onSelect: selectFeatureOnMap,
    })
  }

  const renderProjectMapLayers = async () => {
    if (!projectMapConfiguration?.layers || !drawRef.current) return

    for (const layer of projectMapConfiguration.layers) {
      if (layer.geoJsonDownloadUrl) {
        try {
          const response = await axios.get(layer.geoJsonDownloadUrl, {
            headers: {
              'Content-Type': 'application/geo+json',
            },
          })

          addLayerToMap(layer.id, response.data, layer.visible)
        } catch (error) {
          console.error(
            `Error fetching or adding GeoJSON layer from ${layer.geoJsonDownloadUrl}:`,
            error,
          )
        }
      }
    }
  }

  const renderMaxBoundsBox = () => {
    if (!projectMapConfiguration?.maxBounds) return

    const maxBounds = new LngLatBounds([
      [
        projectMapConfiguration?.maxBounds.southwest.longitude,
        projectMapConfiguration?.maxBounds.southwest.latitude,
      ],
      [
        projectMapConfiguration?.maxBounds.northeast.longitude,
        projectMapConfiguration?.maxBounds.northeast.latitude,
      ],
    ])

    const maxBoundsPolygon = polygon([
      [
        [maxBounds.getSouthWest().lng, maxBounds.getSouthWest().lat],
        [maxBounds.getSouthWest().lng, maxBounds.getNorthEast().lat],
        [maxBounds.getNorthEast().lng, maxBounds.getNorthEast().lat],
        [maxBounds.getNorthEast().lng, maxBounds.getSouthWest().lat],
        [maxBounds.getSouthWest().lng, maxBounds.getSouthWest().lat],
      ],
    ])
    maxBoundsPolygon.id = 'max-bounds-polygon'

    drawRef.current?.add(maxBoundsPolygon)
  }

  const handleOnLoadMap = async (e: MapEvent) => {
    mapRef.current?.addControl(drawRef.current)

    drawRef.current?.changeMode(PMDrawMode.SELECT_FEATURE, {
      onSelect: selectFeatureOnMap,
    })

    renderProjectMapLayers()

    renderMaxBoundsBox()
  }

  const handleOnBack = () =>
    router.push(PageUrls.projectSettings({ organisationId, projectId }))

  return (
    <Box flex={1}>
      <Card
        sx={{
          p: 2,
          borderRadius: 0,
          width: '100%',
          minHeight: '4.4rem',
        }}
      >
        <Flex alignItems='center' justifyContent='space-between'>
          <Flex alignItems='center' mb={1}>
            <Button
              variant='outlined'
              size='medium'
              startIcon={<ArrowBack />}
              sx={{
                '.MuiButton-startIcon': { margin: 0 },
                py: 0.3,
                px: 0.9,
                minWidth: 0,
                mr: 2,
              }}
              onClick={handleOnBack}
            />

            <PageTitle
              sx={{
                m: 0,
                mr: 1,
              }}
              title={`${project?.name} ${localizedStrings.map}`}
            />
            <Box flexGrow={1} />
          </Flex>
          <Flex>
            <IconButton onClick={handleOnBack}>
              <Clear />
            </IconButton>
          </Flex>
        </Flex>
      </Card>

      <Flex flexDirection='row' p={1}>
        <Flex flex={2} flexDirection='column'>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleOnTabChange}
              variant='fullWidth'
              aria-label='map configurator tabs'
            >
              <Tab label='Map' value={0} />
              <Tab label='Disclaimer' value={1} />
              <Tab label='Layers' value={2} />
            </Tabs>
          </Box>

          <MapTabPanel
            project={project}
            active={activeTab === PMConfiguratorTab.MAP}
            projectMapConfiguration={projectMapConfiguration}
          />

          <DisclaimerTabPanel
            project={project}
            active={activeTab === PMConfiguratorTab.DISCLAIMER}
            projectMapConfiguration={projectMapConfiguration}
          />

          <LayersTabPanel
            ref={layersTabRef}
            active={activeTab === PMConfiguratorTab.LAYERS}
          />
        </Flex>

        <Flex flex={3} flexDirection='column' p={1}>
          {activeTab === PMConfiguratorTab.LAYERS && (
            <Flex
              flexDirection='row'
              alignItems='center'
              justifyContent='center'
            >
              {flags.web_project_map_configurator_edit_mode ? (
                <Button
                  variant='outlined'
                  color={
                    drawMode === PMDrawMode.SELECT_FEATURE ? 'primary' : 'error'
                  }
                  onClick={handleOnToggleEditMode}
                >
                  {drawMode === PMDrawMode.SELECT_FEATURE
                    ? localizedStrings.exitEditMode
                    : localizedStrings.enterEditMode}
                </Button>
              ) : null}
            </Flex>
          )}

          <Box mt={1}>
            <Map
              mapRef={mapRef}
              projectId={projectId}
              serverConfig={{
                maxBounds: false,
                layers: false,
                disclaimer: false,
              }}
              disclaimer={projectMapConfiguration?.disclaimer}
              interactive={true}
              onLoad={handleOnLoadMap}
              style={{ height: '70dvh', width: '100%' }}
            />
          </Box>
        </Flex>

        {activeTab === PMConfiguratorTab.LAYERS && (
          <Flex flex={1} flexDirection='column' p={2}>
            <FeaturePropertyEditor />
          </Flex>
        )}
      </Flex>
    </Box>
  )
}
